import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import { getAdminUsers, grantModeratorRole, revokeModeratorRole } from '../api/admin';
import { AdminUserDto } from '../types/admin';

const AdminModeratorsPage: React.FC = () => {
  const [users, setUsers] = useState<AdminUserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminUsers();
      setUsers(data);
    } catch {
      setError('Failed to load admin users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleToggle = async (user: AdminUserDto) => {
    setSaving(user.id);
    try {
      if (user.isModerator) {
        await revokeModeratorRole(user.id);
      } else {
        await grantModeratorRole(user.id);
      }
      setUsers(prev =>
        prev.map(u => u.id === user.id ? { ...u, isModerator: !u.isModerator } : u)
      );
    } catch {
      setError('Failed to update moderator status.');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="min-h-screen bg-surface-container-lowest font-sans antialiased">
      <TopAppBar />
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/admin" className="text-primary hover:underline text-sm">Admin</Link>
          <span className="text-outline">/</span>
          <h1 className="text-3xl font-bold text-on-surface">Moderators</h1>
        </div>

        <p className="text-sm text-on-surface-variant mb-6">
          Moderators receive notifications when contractor profiles require content review and can approve or blacklist profiles.
        </p>

        {error && <p className="text-error mb-4">{error}</p>}

        <div className="bg-surface rounded-2xl shadow-sm border border-outline-variant overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-on-surface-variant">Loading...</div>
          ) : users.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-on-surface-variant">No admin users found.</div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-surface-container">
                <tr>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Email</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Name</th>
                  <th className="py-3 px-4 text-center text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Moderator</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant">
                {users.map(user => (
                  <tr key={user.id} className="hover:bg-surface-container-lowest">
                    <td className="py-3 px-4 text-sm text-on-surface">{user.email}</td>
                    <td className="py-3 px-4 text-sm text-on-surface">
                      {user.firstName || user.lastName
                        ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
                        : <span className="text-on-surface-variant">-</span>
                      }
                    </td>
                    <td className="py-3 px-4 text-center">
                      <input
                        type="checkbox"
                        checked={user.isModerator}
                        disabled={saving === user.id}
                        onChange={() => handleToggle(user)}
                        className="cursor-pointer"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
      <BottomNavBar />
    </div>
  );
};

export default AdminModeratorsPage;
